import { AdminRoles } from "@bp/pim-auth-constants";
import { Button, Input, Modal, Select } from "@bp/ui-components";
import type { SelectOptionType } from "@bp/ui-components/dist/@bp/lib/Select/Select";
import { useState } from "react";
import type { MultiValue, SingleValue } from "react-select";
import { useMutation } from "urql";
import { type FragmentType, getFragmentData, graphql } from "../../../client/gql";
import { sendInvitation } from "../../../utils/invitation";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import css from "../orgList.module.scss";

// Colocated fragment: the org fields the edit form prefills from.
const OrgFormModal_Organization = graphql(`
  fragment OrgFormModal_Organization on Organization {
    uuid
    name
    zipCode
    city
    region
    country
  }
`);

const OrgFormModalCreateOrganization = graphql(`
  mutation OrgFormModal_CreateOrganization($input: [OrganizationCreateInput!]!) {
    createOrganizations(input: $input) {
      organizations {
        uuid
      }
    }
  }
`);

// The organization is only complete with its admin profile, so creation makes
// both. The profile is created without a nested User: the auth-server invitation
// resolves the global admin@tks.eu user and links it (the same flow bp-frontend
// uses), so no duplicate account is minted for that address.
const OrgFormModalCreateAdminProfile = graphql(`
  mutation OrgFormModal_CreateAdminProfile($input: [ProfileCreateInput!]!) {
    createProfiles(input: $input) {
      profiles {
        uuid
      }
    }
  }
`);

const OrgFormModalUpdateOrganization = graphql(`
  mutation OrgFormModal_UpdateOrganization(
    $where: OrganizationWhere
    $update: OrganizationUpdateInput
  ) {
    updateOrganizations(where: $where, update: $update) {
      organizations {
        uuid
      }
    }
  }
`);

/** Technical admin profile every organization is created with, owned by the global admin@tks.eu user. */
const ADMIN_PROFILE_EMAIL = "admin@tks.eu";
const ADMIN_PROFILE_FIRST_NAME = "Admin";
const ADMIN_PROFILE_LAST_NAME = "TK-Schulsoftware";

type InvitationProduct = "bp" | "planung";

const PRODUCT_OPTIONS: SelectOptionType[] = [
  { value: "bp", label: "Bildungsplattform" },
  { value: "planung", label: "TK-Planung" },
];

type Props = {
  /** When set, the modal edits this organization; otherwise it creates a new one. */
  org?: FragmentType<typeof OrgFormModal_Organization>;
  onClose: () => void;
  onSaved: (uuid?: string) => void;
};

export function OrgFormModal({ org: orgRef, onClose, onSaved }: Props) {
  const org = getFragmentData(OrgFormModal_Organization, orgRef);
  const isEdit = Boolean(org);
  const [name, setName] = useState(org?.name ?? "");
  const [zipCode, setZipCode] = useState(org?.zipCode ?? "");
  const [city, setCity] = useState(org?.city ?? "");
  const [region, setRegion] = useState(org?.region ?? "");
  const [country, setCountry] = useState(org?.country ?? "");
  // Product the org's admin profile is invited to; chosen at creation.
  const [adminProduct, setAdminProduct] = useState<InvitationProduct>("bp");
  const [busy, setBusy] = useState(false);
  const [, createOrg] = useMutation(OrgFormModalCreateOrganization);
  const [, createAdminProfile] = useMutation(OrgFormModalCreateAdminProfile);
  const [, updateOrg] = useMutation(OrgFormModalUpdateOrganization);

  const valid = name.trim() !== "";

  /** Creates the admin@tks.eu profile for a fresh org and invites it via the auth-server. */
  const createAdminForOrganization = async (organizationUuid: string): Promise<boolean> => {
    const profileRes = await createAdminProfile({
      input: [
        {
          firstName: ADMIN_PROFILE_FIRST_NAME,
          lastName: ADMIN_PROFILE_LAST_NAME,
          email: ADMIN_PROFILE_EMAIL,
          organization: { connect: { where: { node: { uuid: organizationUuid } } } },
          roles: {
            Organization: {
              connect: [
                {
                  edge: { names: [AdminRoles.OrganizationAdmin] },
                  where: { node: { uuid: organizationUuid } },
                },
              ],
            },
          },
        },
      ],
    });
    if (profileRes.error) {
      showUserErrorToast({
        text: "Organisation angelegt, aber Admin-Profil fehlgeschlagen",
        error: profileRes.error,
      });
      return false;
    }
    const adminProfileUuid = profileRes.data?.createProfiles?.profiles?.[0]?.uuid;
    if (!adminProfileUuid) {
      return true;
    }
    try {
      await sendInvitation(adminProfileUuid, ADMIN_PROFILE_EMAIL, adminProduct);
    } catch (error) {
      showUserErrorToast({
        text: "Organisation angelegt, aber Admin-Einladung fehlgeschlagen",
        error,
      });
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!valid) {
      return;
    }
    const t = {
      name: name.trim(),
      zipCode: zipCode.trim(),
      city: city.trim(),
      region: region.trim(),
      country: country.trim(),
    };
    setBusy(true);
    try {
      if (isEdit) {
        const res = await updateOrg({ where: { uuid: org?.uuid }, update: t });
        if (res.error) {
          showUserErrorToast({ text: "Speichern fehlgeschlagen", error: res.error });
          return;
        }
        showSuccessToast(`${t.name} gespeichert`);
        onSaved(org?.uuid);
        return;
      }

      const res = await createOrg({
        input: [
          {
            name: t.name,
            locale: "de",
            timezone: "Europe/Berlin",
            ...(t.zipCode ? { zipCode: t.zipCode } : {}),
            ...(t.city ? { city: t.city } : {}),
            ...(t.region ? { region: t.region } : {}),
            ...(t.country ? { country: t.country } : {}),
          },
        ],
      });
      if (res.error) {
        showUserErrorToast({ text: "Speichern fehlgeschlagen", error: res.error });
        return;
      }
      const newUuid = res.data?.createOrganizations?.organizations?.[0]?.uuid;
      if (!newUuid) {
        showUserErrorToast({
          text: "Organisation angelegt, aber keine UUID erhalten",
          error: res.error,
        });
        return;
      }

      const adminCreated = await createAdminForOrganization(newUuid);
      showSuccessToast(
        adminCreated ? `${t.name} angelegt` : `${t.name} angelegt — Admin-Profil prüfen`,
      );
      onSaved(newUuid);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      hideFooter={false}
      width="s"
      title={isEdit ? "Organisation bearbeiten" : "Organisation anlegen"}
      onRequestClose={onClose}
      footer={
        <div className={css.foot}>
          <span className={css.footHint}>Stammdaten & Verträge liegen im Orga-Portal.</span>
          <div className={css.footActions}>
            <Button hierarchy="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button hierarchy="primary" disabled={!valid || busy} onClick={() => void submit()}>
              {isEdit ? "Speichern" : "Anlegen"}
            </Button>
          </div>
        </div>
      }
    >
      <div className={css.stack}>
        <Input
          name="name"
          label="Name"
          value={name}
          autoFocus
          placeholder="z. B. Freie Waldorfschule Musterstadt"
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          name="zipCode"
          label="PLZ"
          value={zipCode}
          onChange={(e) => setZipCode(e.target.value)}
        />
        <Input name="city" label="Ort" value={city} onChange={(e) => setCity(e.target.value)} />
        <Input
          name="region"
          label="Bundesland / Region"
          value={region}
          onChange={(e) => setRegion(e.target.value)}
        />
        <Input
          name="country"
          label="Land"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
        />
        {!isEdit && (
          <>
            <Select
              name="adminProduct"
              label="Admin-Einladung (Produkt)"
              options={PRODUCT_OPTIONS}
              value={PRODUCT_OPTIONS.find((o) => o.value === adminProduct) ?? null}
              isSearchable={false}
              onChange={(opt: MultiValue<SelectOptionType> | SingleValue<SelectOptionType>) =>
                setAdminProduct(String((opt as SelectOptionType).value) as InvitationProduct)
              }
            />
            <span className={css.footHint}>
              Das Profil admin@tks.eu wird mit der Organisation angelegt und für dieses Produkt
              eingeladen.
            </span>
          </>
        )}
      </div>
    </Modal>
  );
}
